import {Button, Col, Divider, List, Row, Spin, Tooltip, Typography} from "antd";
import React, {useContext} from "react";
import {
    BrdRestrictionStatus,
    ComplianceEngineResultFragmentFragment,
    ComplianceEngineStatus,
    ComplianceProviderResultFragmentFragment,
    PaxNodeComplianceAuthorizationFieldsFragmentFragment,
    PaxNodeComplianceFieldsWithAuthFragmentFragment,
    ProviderType
} from "__generated__/graphql/types";
import {ButtonProps} from "antd/lib/button";
import './style.less';
import classNames from "classnames";
import {CMContext} from "../compliance-manager/context-provider";
import {scanForProviders} from "../providers/provider-scanner";
import {PaxContext} from "../types";
import {BulkComplianceCheckerContext} from "../../../context/compliance/bulk-compliance-checker";
import {TooltipProps} from "antd/lib/tooltip";
import useBreakpoints from "../../../hooks/useBreakpoints";

const clsPrefix = 'mc-compliance-status-button';

function getStatusClassSuffix(
    auth?: PaxNodeComplianceAuthorizationFieldsFragmentFragment,
    checkStatus?: ComplianceEngineStatus
){


    if (!checkStatus){
        return "unknown";
    }

    if (auth?.brdRestriction_Compliance?.status === BrdRestrictionStatus.OVERRIDDEN) {
        return "overridden";
    }

    switch (checkStatus){
        case ComplianceEngineStatus.PASS:
            return "pass";
        case ComplianceEngineStatus.FAIL:
            return "fail";
        case ComplianceEngineStatus.ERROR:
            return "error";
        case ComplianceEngineStatus.INCONCLUSIVE:
            return "checking";
        default:
            return "default";
    }
}

// Function to determine button properties
function getButtonProps(buttonProps: ComplianceCheckStatusButtonProps): ButtonProps {
    // Minimal mode: status provided without pax
    const isMinimal = !buttonProps?.pax && buttonProps?.status !== undefined;
    const isNoData = !buttonProps?.pax && !buttonProps?.status;

    let text: string | undefined;
    let statusSuffix: string = 'default';

    if (isMinimal || isNoData) {
        statusSuffix = getStatusClassSuffix(undefined, buttonProps.status);
        text = buttonProps.status !== undefined ? String(buttonProps.status) : 'NO RESULT';
        return {
            children: <strong>{text}</strong>,
            className: classNames(
                clsPrefix,
                `${clsPrefix}-${statusSuffix}`,
                buttonProps?.className
            ),
        };
    }

    const pax = buttonProps.pax as (PaxContext | PaxNodeComplianceFieldsWithAuthFragmentFragment | undefined);
    const isPaxContext = !!pax && typeof pax === 'object' && 'paxNode' in (pax as any);

    const paxNode = isPaxContext
        ? (pax as PaxContext).paxNode
        : (pax as PaxNodeComplianceFieldsWithAuthFragmentFragment);

    const complianceResult: ComplianceEngineResultFragmentFragment | undefined = isPaxContext
        ? (pax as PaxContext).complianceResult
        : (pax as PaxNodeComplianceFieldsWithAuthFragmentFragment)?.lastComplianceEngineResult;

    const providerResults = scanForProviders(complianceResult);

    const selectedProviderResult = providerResults.find(
        (s) => s.providerType === buttonProps.selectedProvider
    );

    if (selectedProviderResult){
        statusSuffix = getStatusClassSuffix(undefined, selectedProviderResult?.checkStatus);
        if (selectedProviderResult.reason?.match(/^No \w+ ID/)){
            // No ID was configured for this provider
            text = 'NO ID';
            statusSuffix = 'fail';
        }
        else {
            // selectedProviderResult.checkStatus is an enum/string-like
            text = (selectedProviderResult.checkStatus || 'NO RESULT') as unknown as string;
        }
    }
    else
    {
        if (paxNode?.authorization?.brdRestriction_Compliance?.status === BrdRestrictionStatus.OVERRIDDEN) {
            text = "OVERRIDDEN";
            statusSuffix = "overridden";
        }
        else {
            statusSuffix = getStatusClassSuffix(paxNode?.authorization, complianceResult?.checkStatus);
            text = complianceResult?.checkStatus as unknown as string;
        }
    }

    if (!text) {
        text = "NO RESULT";
    }

    return {
        children: <strong>{text}</strong>,
        className: classNames(
            clsPrefix,
            `${clsPrefix}-${statusSuffix}`,
            buttonProps?.className)
        ,
    };
}

interface ComplianceCheckStatusButtonProps extends ButtonProps {
    pax?: PaxContext | PaxNodeComplianceFieldsWithAuthFragmentFragment;
    status?: ComplianceEngineStatus;
    checking?: boolean;
    style?: React.CSSProperties;
    showSourcesTooltip?: boolean,
    selectedProvider?: ProviderType,
    disabled?: boolean,
    tooltipPlacement?: TooltipProps['placement']
}

/**
 * Displays a button that is colored and contains text according to the compliance status.
 */
const ComplianceCheckStatusButton = React.forwardRef<Button, ComplianceCheckStatusButtonProps>(
    function ComplianceStatusButton(props, ref){

        const bp = useBreakpoints();

        // Use the Compliance Manager context if available
        const complianceManagerCtx = useContext(CMContext);

        // Use bulk compliance checker context if available
        const bulkComplianceCheckerCtx = useContext(BulkComplianceCheckerContext);

        // Minimal mode if status is provided without pax
        const isMinimal = !props.pax && props.status !== undefined;
        const isNoData = !props.pax && props.status === undefined;

        let showSourcesTooltip = props.showSourcesTooltip !== undefined ? props.showSourcesTooltip : true;

        if (bp.max('medium')){
            // Disable tooltip on small screens
            showSourcesTooltip = false;
        }

        if (isMinimal || isNoData) {
            // No tooltip in minimal/no-data mode
            showSourcesTooltip = false;
        }

        // Previously returned null here; now render a minimal, non-clickable "NO RESULT" state.
        if (isNoData) {
            const btnProps = getButtonProps(props);
            // Still apply classNames and text, but don't render a clickable button or tooltip.
            return <Button {...props} {...btnProps} />
        }

        if (!props.pax && !isMinimal) return null;

        const pax = props.pax as (PaxContext | PaxNodeComplianceFieldsWithAuthFragmentFragment | undefined);
        const isPaxContext = !!pax && typeof pax === 'object' && 'paxNode' in (pax as any);
        const paxNode = isPaxContext ? (pax as PaxContext).paxNode : (pax as PaxNodeComplianceFieldsWithAuthFragmentFragment);

        const complianceResult = isPaxContext ?
            (props.pax as PaxContext).complianceResult :
            (pax as PaxNodeComplianceFieldsWithAuthFragmentFragment)?.lastComplianceEngineResult;

        let clickHandler = (event: React.MouseEvent<HTMLElement, MouseEvent>) => {
            if (typeof props.onClick === 'function'){
                props.onClick(event);
            }
            else if (complianceManagerCtx && paxNode?._id) {
                // Open the compliance manager via context if available
                complianceManagerCtx.open(paxNode?._id);
            }
        }

        let btnProps = getButtonProps(props);

        if (!isMinimal && (props.checking || (paxNode && bulkComplianceCheckerCtx ? bulkComplianceCheckerCtx.isLoading(paxNode._id) : false))) {
            return <Spin />;
        }

        if (props.style) {
            btnProps = {
                ...btnProps,
                style: {
                    ...btnProps?.style,
                    ...props.style,
                },
            };
        }

        const renderProvider = (result: ComplianceProviderResultFragmentFragment) => {
            const suffix = getStatusClassSuffix(paxNode?.authorization, result?.checkStatus);

            return (
                <List.Item
                    extra={
                        <Row type="flex" gutter={12}>
                            <Col>
                                <span
                                    style={{ marginLeft: '1rem' }}
                                    className={`mc-compliance-tooltip-${suffix}`}
                                >
                                    <strong>{result.checkStatus}</strong>
                                            {result.reason ? (
                                                ` (Reason: ${result.reason})`
                                            ) : ''}
                                </span>
                            </Col>
                        </Row>
                    }
                >
                    <span>{result.providerType} Status:</span>
                </List.Item>
            )
        }

        const providerResults = scanForProviders(complianceResult);

        const tooltipText = (
            <div>
                <div style={{ textAlign: 'center' }}>
                    <Typography.Text strong>Compliance Providers</Typography.Text>
                </div>
                <Divider style={{ margin: '3px 0 3px 0' }} />
                {providerResults?.length ? (
                    <List size="small">
                        {providerResults.map(renderProvider)}
                    </List>
                ) : (
                    <span>No results available. Try running a compliance check first.</span>
                )}
                <Divider style={{ margin: '3px 0 3px 0' }} />
                <div style={{textAlign: 'center'}}>
                    <Typography.Text strong>Click to view more</Typography.Text>
                </div>
            </div>
        );

        if (props.disabled){
            const cn = classNames(
                btnProps.className,
                clsPrefix + '-disabled'
            )
            return <span className={cn}>{btnProps.children}</span>
        }

        const button = <Button {...props} {...btnProps} ref={ref} onClick={clickHandler} />

        if (showSourcesTooltip){
            return (
                <Tooltip
                    title={tooltipText}
                    placement={props.tooltipPlacement}
                    overlayStyle={{minWidth: 200, maxWidth: 1000 }}
                    trigger="hover"
                >
                    {button}
                </Tooltip>
            );
        }

        return button;
    }
);

ComplianceCheckStatusButton.defaultProps = {
    tooltipPlacement: 'top'
}

export default ComplianceCheckStatusButton;
